/**
 * タグ体系の大規模整理に伴うレガシーURL→新URLへの301リダイレクト。
 * タグの名称変更・統合・削除により旧URLのリンクが切れるため、
 * SEOと既存の外部リンク・ブックマークを維持する目的で設置。
 */

// カテゴリ（theme）の単純リダイレクトマップ: 旧web-path → 新web-path
const themeRedirects: Record<string, string> = {
  'katana-knife': 'knives-and-katana',
  'temple-and-shrine': 'temples-and-shrines',
  'martial-arts': 'budo-martial-arts',
  castle: 'castles',
  lacquer: 'lacquerware',
  'ukiyo-e-woodblock-printing': 'ukiyo-e-printing',
  tea: 'matcha-and-tea',
  'sake-brewery-and-sake-tasting': 'sake-and-alcohol-tastings',
  confectionary: 'wagashi-sweets',
  'cooking-class': 'cooking-classes',
  'city-tour': 'city-walks',
  architecture: 'architecture-and-design',
  museum: 'art-and-museums',
  'japanese-garden': 'japanese-gardens',
  // タグ統合
  kabuki: 'kabuki-and-noh',
  'noh-theater': 'kabuki-and-noh',
  dyeing: 'textile-crafts',
  weaving: 'textile-crafts',
  // 削除 → 特定タグ
  'vegetarian-cuisine': 'shojin-cuisine',
  // 削除 → 統合（Experiential Stays）
  hotels: 'experiential-stays',
  'ryokan-resorts': 'experiential-stays',
  villas: 'experiential-stays',
  'unique-lodgings': 'experiential-stays',
  // 改名（JCAT-4853: Accommodation & Experiential Stays → Experiential Stays）
  'accommodation-and-experiential-stays': 'experiential-stays',
};

// エリア（destination）リダイレクトマップ: 旧web-path → 新web-path
const destinationRedirects: Record<string, string> = {
  'hakone-odawara': 'hakone',
  gifu: 'hida-takayama',
  'hiroshima-onomichi': 'hiroshima',
  'naoshima-and-around': 'naoshima-islands',
};

// 削除タグ → タググループ（全子タグ選択）のリダイレクトマップ
const cultureAndTraditionsThemes = 'tea-ceremony,zen-meditation,temples-and-shrines,castles,kimono,calligraphy,budo-martial-arts,ikebana,sumo,kabuki-and-noh,geisha,traditional-music';
const craftsmanshipThemes = 'kintsugi,pottery-ceramics,chopstick,knives-and-katana,textile-crafts,lacquerware,ukiyo-e-printing,bonsai,sumi-e-ink-painting,japanese-gardens';
const foodAndGastronomyThemes = 'sushi,kaiseki,matcha-and-tea,cooking-classes,sake-and-alcohol-tastings,wagashi-sweets,shojin-cuisine,farm-tours';

// 注意: ここに登録するのは「削除済みタグ」の web-path のみ。
// 現役のテーマタグを登録すると、そのタグでの検索が 301 でグループ全体検索に書き換えられてしまう。
// （`incense` は JCAT-4775/4778 で現役テーマタグとして追加されているため、ここには含めない）
const themeGroupRedirects: Record<string, string> = {
  'other-culture': cultureAndTraditionsThemes,
  'nighttime-activities': cultureAndTraditionsThemes,
  'other-crafts': craftsmanshipThemes,
  'other-art': 'art-and-museums',
  fermentation: foodAndGastronomyThemes,
  'other-cuisine': foodAndGastronomyThemes,
  'market-tour': foodAndGastronomyThemes,
};

// 有効なエリアweb-path（現在のDB + リダイレクト元の旧web-path）
const currentDestinations = [
  'central-japan-others',
  'fukui',
  'hakone',
  'hida-takayama',
  'hiroshima',
  'hiroshima-others',
  'kagawa',
  'kamakura',
  'kanazawa',
  'kanazawa-others',
  'kiso',
  'kurashiki',
  'kyoto',
  'kyoto-osaka-others',
  'matsumoto',
  'mt-fuji-around',
  'mt-fuji-hakone-others',
  'nagoya',
  'naoshima-islands',
  'nara',
  'niigata',
  'okayama-naoshima-others',
  'onomichi',
  'osaka',
  'tokyo',
  'tokyo-others',
  'toyama',
];

export const validDestinations = new Set([...currentDestinations, ...Object.keys(destinationRedirects)]);

export type ResolvedTheme = { type: 'simple'; newTheme: string } | { type: 'group'; themes: string };

export const resolveTheme = (theme: string): ResolvedTheme | null => {
  if (Object.prototype.hasOwnProperty.call(themeRedirects, theme)) {
    return { type: 'simple', newTheme: themeRedirects[theme] };
  }
  if (Object.prototype.hasOwnProperty.call(themeGroupRedirects, theme)) {
    return { type: 'group', themes: themeGroupRedirects[theme] };
  }
  return null;
};

export const resolveDestination = (area: string): string | null => {
  return Object.prototype.hasOwnProperty.call(destinationRedirects, area) ? destinationRedirects[area] : null;
};
